/**
 * TimelineRow Component
 * Renders a single truck row with label and timeline bars
 */

import React, { useMemo } from 'react';
import type { TruckTimelineData, TimelineViewport, MousePosition } from '../types';
import { assignLanes } from '../utils';
import { TimelineBars } from './TimelineBars';
import { BASE_ROW_HEIGHT, LANE_HEIGHT, LANE_SPACING } from '../constants';

interface TimelineRowProps {
  truckData: TruckTimelineData;
  currentY: number;
  viewport: TimelineViewport;
  mousePosition: MousePosition;
  innerWidth: number;
  chartWidth: number;
  isLastRow: boolean;
  onBarHover: (e: React.MouseEvent, truckData: TruckTimelineData) => void;
  onBarLeave: () => void;
  padding: { top: number; right: number; bottom: number; left: number };
  deviceType: 'desktop' | 'tablet' | 'mobile';
}

export const TimelineRow: React.FC<TimelineRowProps> = ({
  truckData,
  currentY,
  viewport,
  mousePosition,
  innerWidth,
  chartWidth,
  isLastRow,
  onBarHover,
  onBarLeave,
  padding,
  deviceType,
}) => {
  // Get responsive values
  const baseRowHeight = BASE_ROW_HEIGHT[deviceType];
  const laneHeight = LANE_HEIGHT[deviceType];

  // Assign lanes to periods
  const periodsWithLanes = useMemo(() => assignLanes(truckData.periods), [truckData.periods]);

  // Calculate row height
  const maxLane = periodsWithLanes.reduce((max, p) => Math.max(max, p.lane), 0);
  const lanesNeeded = maxLane + 1;
  const rowHeight = Math.max(baseRowHeight, lanesNeeded * (laneHeight + LANE_SPACING));
  const labelY = currentY + rowHeight / 2;

  // Responsive font size
  const fontSize = deviceType === 'mobile' ? 11 : deviceType === 'tablet' ? 12 : 13;

  // Create map of active group IDs (entire 89-day bar groups)
  const activeGroupIds = useMemo(() => {
    const ids = new Set<string>();
    if (mousePosition.date) {
      periodsWithLanes.forEach((p) => {
        if (
          p.groupId &&
          mousePosition.date! >= p.startDate &&
          mousePosition.date! <= p.endDate
        ) {
          ids.add(p.groupId);
        }
      });
    }
    return ids;
  }, [periodsWithLanes, mousePosition.date]);

  return (
    <g>
      {/* Truck name label - NO CLIP PATH */}
      <text
        x={padding.left - 10}
        y={labelY + 4}
        textAnchor="end"
        fontSize={fontSize}
        fill="#1f2937"
        fontWeight="500"
        fontFamily="system-ui, -apple-system, sans-serif"
      >
        {truckData.truckName}
      </text>

      {/* Timeline periods (bars) - now in lanes - WITH CLIP PATH */}
      <TimelineBars
        periods={periodsWithLanes}
        viewport={viewport}
        innerWidth={innerWidth}
        currentY={currentY}
        activeGroupIds={activeGroupIds}
        truckData={truckData}
        onBarHover={onBarHover}
        onBarLeave={onBarLeave}
        padding={padding}
        deviceType={deviceType}
      />

      {/* Row separator */}
      {!isLastRow && (
        <line
          x1={padding.left}
          y1={currentY + rowHeight}
          x2={chartWidth - padding.right}
          y2={currentY + rowHeight}
          stroke="#f3f4f6"
          strokeWidth={1}
        />
      )}
    </g>
  );
};
